/**
 * Consultation Popup Modal, Validation & Dialogs Module (English)
 *
 * Handles:
 * - Opening & closing the consultation form modal
 * - Dedicated Success + Thank You Modal
 * - Dedicated Error Modal
 * - Client-side validation with English feedback
 * - Anti-spam tokens & AJAX submission
 *
 * @package Gina_Theme
 */

window.GinaConsultationModal = (function() {
  'use strict';

  let modalEl = null;
  let successModalEl = null;
  let errorModalEl = null;
  let formEl = null;
  let submitBtn = null;
  let statusBox = null;

  function init() {
    modalEl = document.getElementById('consultation-modal');
    successModalEl = document.getElementById('consultation-success-modal');
    errorModalEl = document.getElementById('consultation-error-modal');

    if (!modalEl) return;

    formEl = document.getElementById('consultation-form');
    submitBtn = document.getElementById('consultation-submit-btn');
    statusBox = document.getElementById('consultation-status-box');

    bindTriggers();
    bindFormSubmit();
  }

  function bindTriggers() {
    // Open triggers: buttons or links with data attributes or hrefs
    document.addEventListener('click', function(e) {
      const openBtn = e.target.closest(
        '[data-open-consultation-modal], ' +
        'a[href="#consultation-modal"], ' +
        'a[href="#consultation"], ' +
        '[data-analytics-action="start_project"], ' +
        '[data-analytics-action="start_your_project"], ' +
        '[data-analytics-action="consultation_modal_open"]'
      );
      if (openBtn) {
        e.preventDefault();
        openModal();
        return;
      }

      // Close main form modal
      const closeFormBtn = e.target.closest('[data-close-consultation-modal]');
      if (closeFormBtn) {
        e.preventDefault();
        closeModal();
        return;
      }

      // Close success modal
      const closeSuccessBtn = e.target.closest('[data-close-success-modal]');
      if (closeSuccessBtn) {
        e.preventDefault();
        closeSuccessModal();
        return;
      }

      // Close error modal
      const closeErrorBtn = e.target.closest('[data-close-error-modal]');
      if (closeErrorBtn) {
        e.preventDefault();
        closeErrorModal();
        return;
      }
    });

    // Keyboard support: ESC closes whichever modal is open
    document.addEventListener('keydown', function(e) {
      if (e.key === 'Escape') {
        if (isSuccessModalOpen()) {
          closeSuccessModal();
        } else if (isErrorModalOpen()) {
          closeErrorModal();
        } else if (isModalOpen()) {
          closeModal();
        }
      }
    });
  }

  // --- Modal Helpers ---

  function isModalOpen() {
    return modalEl && !modalEl.classList.contains('pointer-events-none');
  }

  function isSuccessModalOpen() {
    return successModalEl && !successModalEl.classList.contains('pointer-events-none');
  }

  function isErrorModalOpen() {
    return errorModalEl && !errorModalEl.classList.contains('pointer-events-none');
  }

  function openModal() {
    if (!modalEl) return;
    
    // Refresh timestamp anti-spam token on open
    const tsInput = formEl ? formEl.querySelector('input[name="form_timestamp"]') : null;
    if (tsInput) {
      tsInput.value = Math.floor(Date.now() / 1000);
    }

    modalEl.classList.remove('opacity-0', 'pointer-events-none');
    modalEl.setAttribute('aria-hidden', 'false');

    const card = modalEl.querySelector('.consultation-modal-card');
    if (card) {
      card.classList.remove('scale-95');
      card.classList.add('scale-100');
    }

    document.body.style.overflow = 'hidden';

    // Focus first input
    const firstInput = modalEl.querySelector('input[name="name"]');
    if (firstInput) {
      setTimeout(() => firstInput.focus(), 150);
    }
  }

  function closeModal() {
    if (!modalEl) return;

    modalEl.classList.add('opacity-0', 'pointer-events-none');
    modalEl.setAttribute('aria-hidden', 'true');

    const card = modalEl.querySelector('.consultation-modal-card');
    if (card) {
      card.classList.remove('scale-100');
      card.classList.add('scale-95');
    }

    if (!isSuccessModalOpen() && !isErrorModalOpen()) {
      document.body.style.overflow = '';
    }
  }

  // --- Success Modal (Thank You) ---

  function openSuccessModal(message) {
    if (!successModalEl) return;

    if (message) {
      const descEl = successModalEl.querySelector('#success-modal-desc');
      if (descEl) descEl.textContent = message;
    }

    successModalEl.classList.remove('opacity-0', 'pointer-events-none');
    successModalEl.setAttribute('aria-hidden', 'false');

    const card = successModalEl.querySelector('.consultation-modal-card');
    if (card) {
      card.classList.remove('scale-95');
      card.classList.add('scale-100');
    }

    document.body.style.overflow = 'hidden';
  }

  function closeSuccessModal() {
    if (!successModalEl) return;

    successModalEl.classList.add('opacity-0', 'pointer-events-none');
    successModalEl.setAttribute('aria-hidden', 'true');

    const card = successModalEl.querySelector('.consultation-modal-card');
    if (card) {
      card.classList.remove('scale-100');
      card.classList.add('scale-95');
    }

    document.body.style.overflow = '';
  }

  // --- Error Modal ---

  function openErrorModal(message) {
    if (!errorModalEl) return;

    if (message) {
      const msgEl = errorModalEl.querySelector('#consultation-error-modal-msg');
      if (msgEl) msgEl.textContent = message;
    }

    errorModalEl.classList.remove('opacity-0', 'pointer-events-none');
    errorModalEl.setAttribute('aria-hidden', 'false');

    const card = errorModalEl.querySelector('.consultation-modal-card');
    if (card) {
      card.classList.remove('scale-95');
      card.classList.add('scale-100');
    }

    document.body.style.overflow = 'hidden';
  }

  function closeErrorModal() {
    if (!errorModalEl) return;

    errorModalEl.classList.add('opacity-0', 'pointer-events-none');
    errorModalEl.setAttribute('aria-hidden', 'true');

    const card = errorModalEl.querySelector('.consultation-modal-card');
    if (card) {
      card.classList.remove('scale-100');
      card.classList.add('scale-95');
    }

    if (!isModalOpen() && !isSuccessModalOpen()) {
      document.body.style.overflow = '';
    }
  }

  // --- Validation & Field Error Handling ---

  function clearErrors() {
    if (!formEl) return;

    const errorFields = formEl.querySelectorAll('.field-error');
    errorFields.forEach(el => {
      el.classList.remove('field-error', 'border-rose-500');
    });

    const errorMsgs = formEl.querySelectorAll('.field-error-msg, .policy-error-msg');
    errorMsgs.forEach(el => {
      el.textContent = '';
      el.classList.add('hidden');
    });

    if (statusBox) {
      statusBox.className = 'hidden mb-5 p-3.5 rounded-xl text-xs sm:text-sm font-medium border';
      statusBox.innerHTML = '';
    }
  }

  function showFieldError(fieldInput, message) {
    if (!fieldInput) return;
    fieldInput.classList.add('field-error', 'border-rose-500');

    const parent = fieldInput.closest('div');
    if (parent) {
      const msgEl = parent.querySelector('.field-error-msg');
      if (msgEl) {
        msgEl.textContent = message;
        msgEl.classList.remove('hidden');
      }
    }
  }

  function validateForm() {
    clearErrors();

    const nameInput = formEl.querySelector('input[name="name"]');
    const phoneInput = formEl.querySelector('input[name="phone"]');
    const emailInput = formEl.querySelector('input[name="email"]');
    const addressInput = formEl.querySelector('input[name="address"]');
    const industrySelect = formEl.querySelector('select[name="industry"]');
    const scopeSelect = formEl.querySelector('select[name="scope"]');
    const budgetSelect = formEl.querySelector('select[name="budget"]');
    const reqTextarea = formEl.querySelector('textarea[name="requirements"]');
    const policyCheckbox = formEl.querySelector('input[name="policy_agreement"]');

    const errors = [];
    let firstField = null;

    // 1. Name validation
    const nameVal = nameInput ? nameInput.value.trim() : '';
    if (!nameVal || nameVal.length < 2) {
      const err = 'Please enter your full name (minimum 2 characters).';
      showFieldError(nameInput, err);
      errors.push(err);
      if (!firstField) firstField = nameInput;
    }

    // 2. Phone validation
    const phoneVal = phoneInput ? phoneInput.value.trim() : '';
    const phoneClean = phoneVal.replace(/[\s()-]/g, '');
    const phoneRegex = /^[+]?[0-9]{9,15}$/;
    if (!phoneVal || !phoneRegex.test(phoneClean)) {
      const err = 'Please enter a valid phone or WhatsApp number (9 to 15 digits).';
      showFieldError(phoneInput, err);
      errors.push(err);
      if (!firstField) firstField = phoneInput;
    }

    // 3. Email validation
    const emailVal = emailInput ? emailInput.value.trim() : '';
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailVal || !emailRegex.test(emailVal)) {
      const err = 'Please enter a valid work email address.';
      showFieldError(emailInput, err);
      errors.push(err);
      if (!firstField) firstField = emailInput;
    }

    // 4. Address / Location validation
    const addressVal = addressInput ? addressInput.value.trim() : '';
    if (!addressVal || addressVal.length < 2) {
      const err = 'Please enter your location or city.';
      showFieldError(addressInput, err);
      errors.push(err);
      if (!firstField) firstField = addressInput;
    }

    // 5. Industry selection
    const industryVal = industrySelect ? industrySelect.value : '';
    if (!industryVal) {
      const err = 'Please select your website industry.';
      showFieldError(industrySelect, err);
      errors.push(err);
      if (!firstField) firstField = industrySelect;
    }

    // 6. Scope selection
    const scopeVal = scopeSelect ? scopeSelect.value : '';
    if (!scopeVal) {
      const err = 'Please select your project scope.';
      showFieldError(scopeSelect, err);
      errors.push(err);
      if (!firstField) firstField = scopeSelect;
    }

    // 7. Budget selection
    const budgetVal = budgetSelect ? budgetSelect.value : '';
    if (!budgetVal) {
      const err = 'Please select an estimated budget range.';
      showFieldError(budgetSelect, err);
      errors.push(err);
      if (!firstField) firstField = budgetSelect;
    }

    // 8. Requirements validation
    const reqVal = reqTextarea ? reqTextarea.value.trim() : '';
    if (!reqVal || reqVal.length < 5) {
      const err = 'Please enter your project requirements (minimum 5 characters).';
      showFieldError(reqTextarea, err);
      errors.push(err);
      if (!firstField) firstField = reqTextarea;
    }

    // 9. Policy Checkbox
    if (!policyCheckbox || !policyCheckbox.checked) {
      const err = 'You must agree to the privacy policy to proceed.';
      const policyMsg = formEl.querySelector('.policy-error-msg');
      if (policyMsg) {
        policyMsg.textContent = err;
        policyMsg.classList.remove('hidden');
      }
      errors.push(err);
      if (!firstField) firstField = policyCheckbox;
    }

    return {
      isValid: errors.length === 0,
      errors: errors,
      firstField: firstField
    };
  }

  function setLoading(loading) {
    if (!submitBtn) return;
    const spinner = submitBtn.querySelector('.btn-spinner');
    const text = submitBtn.querySelector('.btn-text');
    const icon = submitBtn.querySelector('.btn-icon');

    if (loading) {
      submitBtn.disabled = true;
      if (spinner) spinner.classList.remove('hidden');
      if (text) text.textContent = 'Submitting Request...';
      if (icon) icon.classList.add('hidden');
    } else {
      submitBtn.disabled = false;
      if (spinner) spinner.classList.add('hidden');
      if (text) text.textContent = 'Submit Consultation Request';
      if (icon) icon.classList.remove('hidden');
    }
  }

  function bindFormSubmit() {
    if (!formEl) return;

    // Real-time clearance of field errors on user input
    formEl.addEventListener('input', function(e) {
      if (e.target && e.target.classList.contains('field-error')) {
        e.target.classList.remove('field-error', 'border-rose-500');
        const parent = e.target.closest('div');
        if (parent) {
          const msgEl = parent.querySelector('.field-error-msg');
          if (msgEl) msgEl.classList.add('hidden');
        }
      }
    });

    formEl.addEventListener('change', function(e) {
      if (e.target && e.target.classList.contains('field-error')) {
        e.target.classList.remove('field-error', 'border-rose-500');
        const parent = e.target.closest('div');
        if (parent) {
          const msgEl = parent.querySelector('.field-error-msg');
          if (msgEl) msgEl.classList.add('hidden');
        }
      }
      if (e.target && e.target.name === 'policy_agreement' && e.target.checked) {
        const policyMsg = formEl.querySelector('.policy-error-msg');
        if (policyMsg) policyMsg.classList.add('hidden');
      }
    });

    formEl.addEventListener('submit', function(e) {
      e.preventDefault();

      // 1. Client-side Validation (NO ALERT, ONLY SHOW INLINE ERRORS)
      const validation = validateForm();
      if (!validation.isValid) {
        if (statusBox) {
          statusBox.className = 'mb-5 p-3.5 rounded-xl text-xs sm:text-sm font-medium border bg-rose-500/10 border-rose-500/30 text-rose-300';
          statusBox.innerHTML = '<div class="font-semibold mb-1">Please correct the following fields:</div><ul class="list-disc list-inside space-y-0.5">' + validation.errors.map(err => '<li>' + err + '</li>').join('') + '</ul>';
          statusBox.classList.remove('hidden');
          statusBox.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
        if (validation.firstField) {
          validation.firstField.focus();
        }
        return;
      }

      setLoading(true);

      const formData = new FormData(formEl);
      const ajaxUrl = (window.ginaData && window.ginaData.ajaxUrl) ? window.ginaData.ajaxUrl : '/wp-admin/admin-ajax.php';

      fetch(ajaxUrl, {
        method: 'POST',
        body: formData,
        headers: {
          'X-Requested-With': 'XMLHttpRequest'
        }
      })
      .then(res => res.json())
      .then(data => {
        setLoading(false);

        if (data && data.success) {
          const successMsg = (data.data && data.data.message) 
            ? data.data.message 
            : 'Your consultation request has been submitted successfully. Our engineering team will review your specifications and get in touch within 24 business hours.';

          // 1. Close form modal & Clear errors
          closeModal();
          clearErrors();
          formEl.reset();

          // 2. Open Success + Thank You Modal
          setTimeout(function() {
            openSuccessModal(successMsg);
          }, 200);

        } else {
          const errMsg = (data && data.data && data.data.message) 
            ? data.data.message 
            : 'An error occurred while submitting your request. Please check your details and try again.';

          // Show dedicated Error Modal dialog (NO native alert)
          openErrorModal(errMsg);

          if (statusBox) {
            statusBox.className = 'mb-5 p-3.5 rounded-xl text-xs sm:text-sm font-medium border bg-rose-500/10 border-rose-500/30 text-rose-300';
            statusBox.textContent = errMsg;
            statusBox.classList.remove('hidden');
          }

          if (data && data.data && data.data.errors) {
            for (const [key, msg] of Object.entries(data.data.errors)) {
              const fieldEl = formEl.querySelector('[name="' + key + '"]');
              if (fieldEl) showFieldError(fieldEl, msg);
            }
          }
        }
      })
      .catch(err => {
        setLoading(false);
        const netErrMsg = 'Unable to connect to the server. Please check your internet connection or try again later.';

        // Show dedicated Error Modal dialog (NO native alert)
        openErrorModal(netErrMsg);

        if (statusBox) {
          statusBox.className = 'mb-5 p-3.5 rounded-xl text-xs sm:text-sm font-medium border bg-rose-500/10 border-rose-500/30 text-rose-300';
          statusBox.textContent = netErrMsg;
          statusBox.classList.remove('hidden');
        }
      });

    });
  }

  return {
    init: init,
    open: openModal,
    close: closeModal,
    openSuccess: openSuccessModal,
    closeSuccess: closeSuccessModal,
    openError: openErrorModal,
    closeError: closeErrorModal
  };
})();
